import { QueryClient } from '@tanstack/react-query';
import type { Conversation } from '@/models/conversation';
import type { Message } from '@/models/message';
import { queryKeys } from './queryKeys';

export async function invalidateConversationCollections(queryClient: QueryClient) {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: queryKeys.conversations.list() }),
    queryClient.invalidateQueries({ queryKey: queryKeys.conversations.searchRoot() }),
  ]);
}

export async function invalidateConversationDetail(
  queryClient: QueryClient,
  conversationId: string
) {
  await queryClient.invalidateQueries({
    queryKey: queryKeys.conversations.detail(conversationId),
  });
}

export function removeConversationCaches(queryClient: QueryClient, conversationId: string) {
  queryClient.removeQueries({
    queryKey: queryKeys.conversations.detail(conversationId),
    exact: true,
  });
  queryClient.removeQueries({
    queryKey: queryKeys.conversations.messages(conversationId),
    exact: true,
  });
}

export function patchMessageInConversationCache(
  queryClient: QueryClient,
  conversationId: string,
  messageId: string,
  update: (message: Message) => Message
) {
  queryClient.setQueryData<Message[]>(
    queryKeys.conversations.messages(conversationId),
    (currentMessages) => {
      if (!currentMessages) {
        return currentMessages;
      }

      return currentMessages.map((message) =>
        message.id === messageId ? update(message) : message
      );
    }
  );
}

function patchConversationArray(
  conversations: Conversation[],
  conversationId: string,
  update: (conversation: Conversation) => Conversation
) {
  return conversations.map((conversation) =>
    conversation.id === conversationId ? update(conversation) : conversation
  );
}

export function patchConversationInCaches(
  queryClient: QueryClient,
  conversationId: string,
  update: (conversation: Conversation) => Conversation
) {
  queryClient.setQueryData<Conversation[]>(
    queryKeys.conversations.list(),
    (currentConversations) => {
      if (!currentConversations) {
        return currentConversations;
      }

      return patchConversationArray(currentConversations, conversationId, update);
    }
  );

  queryClient.setQueryData<Conversation>(
    queryKeys.conversations.detail(conversationId),
    (currentConversation) => {
      if (!currentConversation) {
        return currentConversation;
      }

      return update(currentConversation);
    }
  );

  queryClient.setQueriesData(
    { queryKey: queryKeys.conversations.searchRoot() },
    (currentData) => {
      if (!currentData || typeof currentData !== 'object') {
        return currentData;
      }

      const dataWithConversations = currentData as {
        conversations?: Conversation[];
      };

      if (Array.isArray(dataWithConversations.conversations)) {
        return {
          ...dataWithConversations,
          conversations: patchConversationArray(
            dataWithConversations.conversations,
            conversationId,
            update
          ),
        };
      }

      const dataWithPages = currentData as {
        pages?: Array<{ conversations?: Conversation[] }>;
      };

      if (!Array.isArray(dataWithPages.pages)) {
        return currentData;
      }

      return {
        ...dataWithPages,
        pages: dataWithPages.pages.map((page) => {
          if (!Array.isArray(page.conversations)) {
            return page;
          }

          return {
            ...page,
            conversations: patchConversationArray(page.conversations, conversationId, update),
          };
        }),
      };
    }
  );
}